import React from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Tooltip,
  IconButton,
  Button,
  Stack,
  Chip
} from '@mui/material';
import {
  HelpOutline as HelpIcon
} from '@mui/icons-material';

/**
 * FieldRenderer Component
 * Dynamically renders input fields based on master parameter schema metadata
 * 
 * Features:
 * - Type-based rendering (text, number, select, boolean, ip, tone)
 * - Quick-pick buttons from meta.raw.buttons
 * - Min/max validation for numbers
 * - Tooltips from meta.description
 * - Conditional visibility based on requirements
 */
export default function FieldRenderer({
  param,
  value,
  meta,
  deviceConfig,
  onChange,
  disabled = false,
  showOriginal = false,
  originalValue = null
}) {
  /**
   * Check if field should be visible based on requirements
   */
  const isVisible = () => {
    if (!meta?.requirements || meta.requirements === 'NA' || meta.requirements === 'N/A' || meta.requirements === '') {
      return true;
    }

    // Parse requirement: "sip.detect.mode = 1" or "audio.page.mode = 2"
    const requirement = meta.requirements.trim();
    if (!requirement.includes('=')) {
      return true;
    }
    const match = requirement.match(/^(.+?)\s*=\s*["']?(.+?)["']?(?:\s+and\s+.*)?$/);
    
    if (!match) {
      console.warn(`[FieldRenderer] Unable to parse requirement: ${requirement}`);
      return true; // Show by default if can't parse
    }

    const [, reqParam, reqValue] = match;
    const currentValue = deviceConfig?.[reqParam];
    
    // Compare values (handle both string and number comparisons)
    return String(currentValue) === String(reqValue);
  };

  /**
   * Parse quick-pick buttons from meta.raw.buttons
   * Returns array of button values or null if too many (>4)
   */
  const getQuickPickButtons = () => {
    if (!meta?.raw?.buttons) return null;

    // Clean up the buttons string
    let buttonsStr = meta.raw.buttons.trim();
    
    // Remove surrounding quotes if present
    if ((buttonsStr.startsWith('"') && buttonsStr.endsWith('"')) ||
        (buttonsStr.startsWith("'") && buttonsStr.endsWith("'"))) {
      buttonsStr = buttonsStr.slice(1, -1);
    }

    // If empty string, no buttons
    if (buttonsStr === '') return null;

    // Split by comma and trim
    const buttons = buttonsStr.split(',').map(b => b.trim()).filter(b => b !== '');
    
    // If more than 4 buttons, return null (will use dropdown instead)
    if (buttons.length > 4) return null;
    
    // If no buttons, return null
    if (buttons.length === 0) return null;

    return buttons;
  };

  /**
   * Render input based on type
   */
  const renderInput = () => {
    const type = meta?.type || 'text';
    const quickPickButtons = getQuickPickButtons();

    // Handle select type
    if (type === 'select' && meta?.allowedValues && Array.isArray(meta.allowedValues)) {
      return (
        <Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          fullWidth
          disabled={disabled}
          sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
        >
          {meta.allowedValues.map(option => (
            <MenuItem key={option} value={option} sx={{ fontFamily: 'monospace' }}>
              {option}
            </MenuItem>
          ))}
        </Select>
      );
    }

    // Handle boolean/toggle type
    if (type === 'boolean' || (type === 'select' && meta?.allowedValues?.length === 2 && 
        (meta.allowedValues.includes('0') && meta.allowedValues.includes('1')))) {
      const boolValue = value === '1' || value === 'true' || value === 'yes' || value === 'on';
      return (
        <FormControlLabel
          control={
            <Switch
              checked={boolValue}
              onChange={(e) => {
                const newValue = e.target.checked ? '1' : '0';
                onChange(newValue);
              }}
              disabled={disabled}
            />
          }
          label={boolValue ? 'Enabled' : 'Disabled'}
        />
      );
    }

    // Handle number type
    if (type === 'number') {
      return (
        <TextField
          type="number"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          fullWidth
          disabled={disabled}
          inputProps={{
            min: meta?.min,
            max: meta?.max
          }}
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            '& input': { fontFamily: 'monospace', fontSize: '0.875rem' }
          }}
        />
      );
    }

    // Default: text, ip, tone, or any other type
    // Render with quick-pick buttons if available
    return (
      <Box>
        <TextField
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          fullWidth
          disabled={disabled}
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            '& input': { fontFamily: 'monospace', fontSize: '0.875rem' }
          }}
        />
        
        {/* Quick-pick buttons */}
        {quickPickButtons && quickPickButtons.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
            {quickPickButtons.map((btnValue, idx) => (
              <Button
                key={idx}
                size="small"
                variant="outlined"
                onClick={() => onChange(btnValue)}
                disabled={disabled}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontFamily: 'monospace'
                }}
              >
                {btnValue}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  // Check visibility
  if (!isVisible()) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* Label with tooltip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {meta?.label || param}
        </Typography>
        
        {meta?.description && (
          <Tooltip title={meta.description} arrow>
            <IconButton size="small" sx={{ p: 0 }}>
              <HelpIcon fontSize="small" sx={{ fontSize: 16, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        )}
        
        {/* Show section/group info */}
        {meta?.section && (
          <Chip
            label={meta.section}
            size="small"
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        )}
      </Box>

      {/* Parameter name (technical) */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontFamily: 'monospace' }}>
        {param}
      </Typography>

      {/* Input field */}
      {renderInput()}

      {/* Show original value if modified */}
      {showOriginal && originalValue !== null && originalValue !== value && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
          Original: <code>{originalValue}</code>
        </Typography>
      )}
    </Box>
  );
}
